import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Bot, Play, Clock, Sparkles, CheckCircle2, Inbox, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FILTER_PARAMS = {
  all: {},
  schedule: { trigger: "schedule" },
  manual: { trigger: "manual" },
  published: { published: true },
};
const FILTER_LABELS = { all: "All", schedule: "Scheduled", manual: "Manual", published: "Published something" };

const PUBLISHING_PHASES = { all: "Run all phases", review: "Review my drafts", tips: "Triage tips" };

/**
 * AI Agent control panel. Shows last-run summary, lets an admin trigger the
 * three-phase cycle (discover / review / tips / all), and surfaces the
 * historical run log.
 */
export default function AdminAgent() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(null); // "discover" | "review" | "tips" | "all" | null
  const [lastResult, setLastResult] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [confirmPhase, setConfirmPhase] = useState(null);
  const [filter, setFilter] = useState("all");

  const reload = async () => {
    try {
      const [s, h] = await Promise.all([
        api.get("/admin/agent/status"),
        api.get("/admin/agent/history", { params: { limit: 20, ...FILTER_PARAMS[filter] } }),
      ]);
      setStatus(s.data);
      setHistory(h.data.runs || []);
      setLoadError(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not load agent status";
      setLoadError(msg);
      toast.error(msg, { id: "agent-load-error" });
    }
  };

  useEffect(() => { reload(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (phase, dry = false) => {
    setRunning(phase);
    try {
      const start = await api.post("/admin/agent/run", null, { params: { phase, dry_run: dry, background: true } });
      let r = { data: start.data };
      for (let i = 0; i < 200 && r.data.status === "running"; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        r = await api.get(`/admin/agent/runs/${start.data.id}`);
      }
      if (r.data.status === "running") throw new Error("Still running — check history shortly");
      setLastResult(r.data);
      toast.success(`Agent ${phase} ${dry ? "(dry)" : ""} finished`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || `Agent ${phase} failed`);
    } finally {
      setRunning(null);
    }
  };

  const requestRun = (phase, dry = false) => {
    if (!dry && PUBLISHING_PHASES[phase]) setConfirmPhase(phase);
    else run(phase, dry);
  };

  if (!status && loadError) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-20" data-testid="agent-load-error">
        <div className="border border-[#D32F2F] bg-[#FFEBEE] text-[#D32F2F] p-4 font-mono-plex text-sm">{loadError}</div>
        <Button data-testid="agent-retry-btn" onClick={reload} className="rounded-none mt-4 bg-black text-white">Retry</Button>
      </div>
    );
  }

  if (!status) {
    return <div className="max-w-5xl mx-auto px-6 py-20 font-mono-plex text-sm uppercase tracking-wider text-[#555]">Loading…</div>;
  }

  const last = status.last_run;
  const limits = status.limits;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-agent-page">
      <Link to="/admin" className="inline-flex items-center gap-2 font-mono-plex text-sm uppercase tracking-wider hover:underline" data-testid="back-to-admin">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>

      <div className="mt-4 border-b border-black/15 pb-6 flex items-center gap-3">
        <Bot className="w-7 h-7" />
        <div>
          <div className="overline">/ai-agent</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">AI Agent</h1>
          <p className="text-sm text-[#555] mt-1 max-w-2xl">
            A privileged editor-equivalent that discovers articles, verifies citations on your drafts
            (auto-publishing when ≥{limits.min_verified_citations_to_publish} verified sources exist), and triages reader tips.
            Runs daily at 15:00 UTC — plus any run you trigger here. Every action is attributed to{" "}
            <span className="font-mono-plex text-xs">{status.agent_email}</span> in the audit log.
          </p>
        </div>
      </div>

      <AutoPublishControl status={status} onChanged={reload} />

      {/* Status tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-black/15 border border-black/15 mt-6">
        <Tile label="Enrolled" value={status.enrolled ? "yes" : "no"} />
        <Tile label="Auto daily" value={status.auto_daily ? "15:00 UTC" : "off"} />
        <Tile label="Draft cap / run" value={limits.max_drafts_per_run} />
        <Tile label="Tip promote cap" value={limits.auto_promote_tips} />
      </div>

      {/* Trigger buttons */}
      <div className="mt-8 border border-black/15 p-5 bg-white">
        <div className="overline mb-3">/trigger a run</div>
        <div className="flex flex-wrap gap-2">
          <RunBtn label="Run all phases" phase="all" primary icon={<Play className="w-4 h-4 mr-2" />} running={running} onClick={requestRun} />
          <RunBtn label="Discover articles" phase="discover" icon={<Sparkles className="w-4 h-4 mr-2" />} running={running} onClick={requestRun} />
          <RunBtn label="Review my drafts" phase="review" icon={<CheckCircle2 className="w-4 h-4 mr-2" />} running={running} onClick={requestRun} />
          <RunBtn label="Triage tips" phase="tips" icon={<Inbox className="w-4 h-4 mr-2" />} running={running} onClick={requestRun} />
          <Button
            data-testid="agent-run-dry"
            variant="outline"
            disabled={!!running}
            onClick={() => run("discover", true)}
            className="rounded-none border-black h-11"
          >
            Dry-run discover
          </Button>
        </div>
        <p className="text-xs text-[#555] mt-3">
          Runs are capped per cycle (discover: {limits.max_drafts_per_run}, review: {limits.max_reviews_per_run},
          tips: {limits.max_tips_per_run}). Running a phase repeatedly within the same hour is safe — the
          discover step skips URLs it has already seen. Dry runs are shown here only and are not saved to history.
        </p>
      </div>

      <AlertDialog open={!!confirmPhase} onOpenChange={(o) => !o && setConfirmPhase(null)}>
        <AlertDialogContent className="rounded-none" data-testid="agent-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{PUBLISHING_PHASES[confirmPhase]}?</AlertDialogTitle>
            <AlertDialogDescription>
              This run makes live changes.{" "}
              {status.auto_publish
                ? <>The agent may auto-publish drafts with ≥{limits.min_verified_citations_to_publish} verified citations and </>
                : <>Auto-publish is paused, so qualifying drafts are only flagged "Ready to publish"; the agent may still </>}
              reject or promote reader tips. Use a dry run first if unsure.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="agent-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-none bg-black text-white"
              data-testid="agent-confirm-run"
              onClick={() => { const p = confirmPhase; setConfirmPhase(null); run(p, false); }}
            >
              Run now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Latest run detail */}
      {(lastResult || last) && (
        <div className="mt-8 border border-black/15 bg-white p-5" data-testid="agent-last-result">
          <div className="overline mb-2">/latest run</div>
          <RunSummary run={lastResult || last} />
          {!(lastResult || last).dry_run && (
            <Link to={`/admin/agent/runs/${(lastResult || last).id}`} className="inline-flex mt-3 text-xs font-mono-plex uppercase tracking-wider underline" data-testid="agent-last-result-open">
              Open run details →
            </Link>
          )}
        </div>
      )}

      {/* History */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="overline">/history</div>
            <div className="flex gap-1 flex-wrap" role="group" aria-label="Filter runs" data-testid="agent-history-filters">
              {Object.keys(FILTER_PARAMS).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFilter(k)}
                  aria-pressed={filter === k}
                  data-testid={`agent-history-filter-${k}`}
                  className={`rounded-full border border-black px-3 py-1 text-xs font-mono-plex uppercase tracking-wider transition-colors ${filter === k ? "bg-black text-white" : "bg-white hover:bg-black/5"}`}
                >
                  {FILTER_LABELS[k]}
                </button>
              ))}
            </div>
          </div>

        </div>
        {history.length === 0 ? (
          <p className="text-sm text-[#555] border border-dashed border-black/20 p-6">{filter === "all" ? "No runs yet. Trigger one above." : "No runs match this filter."}</p>
        ) : (
          <HistoryScroller>
            {history.map((h) => (
              <details key={h.id} className="p-4" data-testid={`agent-history-${h.id?.slice(0, 8)}`}>
                <summary className="cursor-pointer flex flex-wrap items-start gap-x-3 gap-y-1 break-words">
                  <Clock className="w-4 h-4 text-[#555] mt-0.5 shrink-0" />
                  <span className="font-mono-plex text-xs whitespace-nowrap mt-0.5">{(h.finished_at || h.started_at || "").slice(0, 19).replace("T", " ")} UTC</span>
                  <span className="text-sm min-w-0 flex-1 break-words">
                    discover: <b>{h.discover?.drafted ?? 0}</b>{h.dry_run ? " would draft" : ""} ·
                    review: <b>{h.review?.published?.length ?? 0}</b> published,{" "}
                    <b>{h.review?.citations_verified ?? 0}</b> verified ·
                    tips: <b>{h.tips?.scored ?? 0}</b> scored,{" "}
                    <b>{h.tips?.promoted?.length ?? 0}</b> promoted
                  </span>
                  {h.dry_run && <span className="font-mono-plex text-[10px] uppercase tracking-wider border border-black px-1.5 py-0.5" data-testid="agent-history-dry-badge">dry run</span>}
                  {h.status === "running" && <span className="font-mono-plex text-[10px] uppercase tracking-wider bg-[#0050FF] text-white px-1.5 py-0.5">running</span>}
                  {h.error && <AlertCircle className="w-4 h-4 text-[#D32F2F]" />}
                </summary>
                <div className="mt-3 pl-7 break-words [overflow-wrap:anywhere]">
                  <RunSummary run={h} compact />
                  <Link to={`/admin/agent/runs/${h.id}`} className="inline-flex items-center gap-1 mt-3 text-xs font-mono-plex uppercase tracking-wider underline" data-testid={`agent-history-open-${h.id?.slice(0, 8)}`}>
                    Open run details →
                  </Link>
                </div>
              </details>
            ))}
          </HistoryScroller>
        )}
      </div>
    </div>
  );
}

function HistoryScroller({ children }) {
  const ref = useRef(null);
  const scrollBy = (dx) => ref.current?.scrollBy({ left: dx, behavior: "smooth" });
  return (
    <div className="border border-black/15 bg-white" data-testid="agent-history-list">
      <div className="flex justify-end gap-1 border-b border-black/10 p-1.5">
        <Button variant="outline" size="icon" className="rounded-none h-8 w-8 border-black" onClick={() => scrollBy(-320)} data-testid="agent-history-scroll-left" aria-label="Scroll left">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="icon" className="rounded-none h-8 w-8 border-black" onClick={() => scrollBy(320)} data-testid="agent-history-scroll-right" aria-label="Scroll right">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      <div ref={ref} className="agent-history-scroll overflow-x-auto" data-testid="agent-history-scroll">
        <div className="min-w-[720px] divide-y divide-black/10">{children}</div>
      </div>
    </div>
  );
}

function AutoPublishControl({ status, onChanged }) {
  const [busy, setBusy] = useState(false);
  const on = status.auto_publish !== false;
  const changed = status.auto_publish_changed;
  const toggle = async (next) => {
    setBusy(true);
    try {
      await api.put("/admin/agent/settings", { auto_publish: next });
      toast.success(next ? "Auto-publish resumed" : "Auto-publish paused");
      await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update setting");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className={`mt-6 border p-4 flex items-start justify-between gap-4 flex-wrap ${on ? "border-black/15 bg-white" : "border-[#B26A00] bg-[#FFF4E0]"}`}
      data-testid="agent-auto-publish-panel"
    >
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold text-lg" data-testid="agent-auto-publish-state">
          {on ? "Auto-publish is on" : "Auto-publish paused"}
        </div>
        <p className="text-sm text-[#333] mt-1 max-w-2xl">
          {on
            ? "Drafts with enough verified citations are published by the agent automatically. Drafting and tip triage always run."
            : "The agent still drafts, verifies citations and triages tips, but qualifying drafts are only flagged \"Ready to publish\" for a human editor."}
        </p>
        {changed?.updated_by && (
          <div className="font-mono-plex text-[11px] text-[#555] mt-2" data-testid="agent-auto-publish-changed">
            Last changed by {changed.updated_by} · {(changed.updated_at || "").slice(0, 16).replace("T", " ")} UTC
          </div>
        )}
      </div>
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <span className="font-mono-plex text-xs uppercase tracking-wider">Auto-publish</span>
        <Switch checked={on} disabled={busy} onCheckedChange={toggle} data-testid="agent-auto-publish-switch" aria-label="Auto-publish" />
      </label>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="bg-white p-5">
      <div className="font-mono-plex text-[11px] uppercase tracking-wider text-[#555]">{label}</div>
      <div className="font-display text-3xl font-black mt-1">{value}</div>
    </div>
  );
}

function RunBtn({ label, phase, icon, running, onClick, primary }) {
  const busy = running === phase;
  const anyBusy = !!running;
  return (
    <Button
      data-testid={`agent-run-${phase}`}
      disabled={anyBusy}
      onClick={() => onClick(phase, false)}
      className={`rounded-none h-11 ${primary ? "bg-black text-white" : "border border-black bg-white text-black hover:bg-black hover:text-white"}`}
    >
      {icon}{busy ? "Running…" : label}
    </Button>
  );
}

function RunSummary({ run, compact = false }) {
  if (!run) return null;
  const d = run.discover;
  const r = run.review;
  const t = run.tips;
  return (
    <div className="space-y-4 text-sm">
      {run.error && (
        <div className="border border-[#D32F2F] bg-[#FFEBEE] text-[#D32F2F] p-3 font-mono-plex text-xs">
          ERROR: {run.error}
        </div>
      )}
      {d && (
        <section>
          <div className="font-display font-bold">Discover</div>
          <div className="text-[#555]">
            {d.candidates} candidates · {d.unseen} new · <b>{d.drafted}</b> {run.dry_run ? "would draft" : "drafted"} · {d.skipped} skipped
          </div>
          {!compact && d.drafts?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {d.drafts.map((x, i) => (
                <li key={i} className="text-xs border-l-2 border-black/20 pl-3">
                  {x.id ? (
                    <>
                      <Link to={`/admin/edit/${x.id}`} className="underline">{x.title}</Link>{" "}
                      <span className="text-[#888]">— {x.verified ? "citation verified" : "unverified"}{x.reason ? ` (${x.reason})` : ""}</span>
                    </>
                  ) : (
                    <>
                      {x.url ? <a href={x.url} target="_blank" rel="noopener noreferrer" className="underline">{x.title || x.url}</a> : x.title}{" "}
                      <span className="text-[#888]">— would draft (dry run)</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {r && (
        <section>
          <div className="font-display font-bold">Review</div>
          <div className="text-[#555]">
            Scanned {r.scanned} · verified <b>{r.citations_verified}</b> citation(s) ·{" "}
            <b>{r.published?.length || 0}</b> auto-published
            {r.ready_to_publish?.length > 0 && <> · <b>{r.ready_to_publish.length}</b> ready to publish</>}
          </div>
          {!compact && r.published?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {r.published.map((x, i) => (
                <li key={i} className="text-xs border-l-2 border-[#008A00] pl-3">
                  <Link to={`/entry/${x.id}`} className="underline">{x.title}</Link>{" "}
                  <span className="text-[#888]">— {x.verified_citations} verified citations</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {t && (
        <section>
          <div className="font-display font-bold">Tips</div>
          <div className="text-[#555]">
            Scored {t.scored} — promising {t.labels?.promising || 0}, duplicate {t.labels?.duplicate || 0},
            off-topic {t.labels?.off_topic || 0} · <b>{t.promoted?.length || 0}</b> promoted
          </div>
          {!compact && t.promoted?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {t.promoted.map((x, i) => (
                <li key={i} className="text-xs border-l-2 border-[#0050FF] pl-3">
                  <Link to={`/admin/edit/${x.entry_id}`} className="underline">{x.title}</Link>{" "}
                  <span className="text-[#888]">— {Math.round(x.confidence * 100)}% confidence</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
